/**
 * SkeletonLoader: Reusable shimmer loading placeholders
 * Replaces plain spinners with layout-aware skeleton states
 * for improved perceived performance.
 */

const Bone = ({
  width = "100%",
  height = 16,
  radius = 8,
  style,
}: {
  width?: string | number;
  height?: number;
  radius?: number;
  style?: React.CSSProperties;
}) => (
  <div
    className="skeleton"
    style={{
      width,
      height,
      borderRadius: radius,
      flexShrink: 0,
      ...style,
    }}
  />
);

/** Metric card skeleton */
function MetricSkeleton() {
  return (
    <div
      className="card"
      style={{
        padding: "var(--spacing-lg)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <Bone width="60%" height={12} />
      <Bone width="45%" height={28} />
      <Bone width="80%" height={10} />
    </div>
  );
}

/** Donut chart placeholder */
function ChartDonutSkeleton() {
  return (
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: "50%",
        flexShrink: 0,
      }}
      className="skeleton"
    />
  );
}

/** Single project card skeleton */
function ProjectCardSkeleton() {
  return (
    <div
      className="card"
      style={{
        padding: "var(--spacing-lg)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--spacing-md)",
      }}
    >
      {/* Header: donut + title + badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <ChartDonutSkeleton />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          <Bone width="70%" height={16} />
          <Bone width="50%" height={12} />
        </div>
        <Bone width={60} height={22} radius={12} />
      </div>

      {/* Budget badge */}
      <Bone width={120} height={20} radius={12} />

      {/* Divider */}
      <div style={{ borderTop: "1px solid var(--border-color)" }} />

      {/* Footer row */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
          <Bone width="60%" height={10} />
          <Bone width="80%" height={16} />
        </div>
        <div style={{ display: "flex", gap: 20 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
            <Bone width={50} height={10} />
            <Bone width={70} height={14} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
            <Bone width={50} height={10} />
            <Bone width={40} height={14} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Dashboard full-page skeleton */
function DashboardSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xl)" }}>
      {/* Title */}
      <div>
        <Bone width={260} height={28} style={{ marginBottom: 8 }} />
        <Bone width={400} height={14} />
      </div>

      {/* AI Insight skeleton */}
      <div className="card" style={{ padding: "var(--spacing-lg)" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <Bone width={40} height={40} radius={10} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            <Bone width="50%" height={14} />
            <Bone width="90%" height={12} />
            <Bone width="70%" height={12} />
          </div>
        </div>
      </div>

      {/* Top 3 metric cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "var(--spacing-lg)",
        }}
      >
        <div className="card" style={{ padding: "var(--spacing-lg)", minHeight: 320, display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <Bone width="50%" height={12} />
          <div className="skeleton" style={{ width: 160, height: 160, borderRadius: "50%" }} />
          <div style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Bone width={80} height={10} />
              <Bone width={100} height={16} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
              <Bone width={80} height={10} />
              <Bone width={100} height={16} />
            </div>
          </div>
        </div>
        <MetricSkeleton />
        <MetricSkeleton />
      </div>

      {/* Monthly summary */}
      <div>
        <Bone width={200} height={18} style={{ marginBottom: 16 }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "var(--spacing-lg)" }}>
          <MetricSkeleton />
          <MetricSkeleton />
          <MetricSkeleton />
        </div>
      </div>

      {/* Charts row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "var(--spacing-lg)" }}>
        <div className="card" style={{ padding: "var(--spacing-lg)" }}>
          <Bone width="55%" height={16} style={{ marginBottom: 16 }} />
          <Bone height={240} />
        </div>
        <div className="card" style={{ padding: "var(--spacing-lg)" }}>
          <Bone width="55%" height={16} style={{ marginBottom: 16 }} />
          <Bone height={240} />
        </div>
      </div>
    </div>
  );
}

/** Projects grid skeleton */
function ProjectsGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xl)" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <Bone width={120} height={24} style={{ marginBottom: 8 }} />
          <Bone width={80} height={12} />
        </div>
        <Bone width={130} height={38} radius={10} />
      </div>

      {/* Search bar */}
      <div style={{ display: "flex", gap: "var(--spacing-md)", flexWrap: "wrap" }}>
        <Bone width={300} height={42} radius={10} style={{ flex: "1 1 250px", maxWidth: 400 }} />
        <Bone width={150} height={42} radius={10} />
      </div>

      {/* Cards grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "var(--spacing-lg)",
        }}
      >
        {Array.from({ length: count }).map((_, i) => (
          <ProjectCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/** Activity feed skeleton */
function ActivitySkeleton({ count = 6 }: { count?: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-lg)" }}>
      {/* Date header */}
      <Bone width={100} height={12} style={{ marginBottom: 4 }} />

      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="card"
          style={{
            padding: "var(--spacing-md)",
            display: "flex",
            alignItems: "flex-start",
            gap: "var(--spacing-md)",
          }}
        >
          <Bone width={32} height={32} radius={16} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            <Bone width="80%" height={14} />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Bone width={70} height={18} radius={4} />
              <Bone width={90} height={18} radius={4} />
              <Bone width={100} height={18} radius={4} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Main export: variant-based skeleton loader */
export function SkeletonLoader({
  variant,
  count,
}: {
  variant: "dashboard" | "projects-grid" | "activity" | "metric" | "project-card";
  count?: number;
}) {
  switch (variant) {
    case "dashboard":
      return <DashboardSkeleton />;
    case "projects-grid":
      return <ProjectsGridSkeleton count={count} />;
    case "activity":
      return <ActivitySkeleton count={count} />;
    case "metric":
      return <MetricSkeleton />;
    case "project-card":
      return <ProjectCardSkeleton />;
    default:
      return <DashboardSkeleton />;
  }
}
